import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import RealEstateCommissionSupportAPI from 'dashboard/api/realEstateCommissionSupport';

const STORAGE_KEY = 'real_estate_commission_support_filters';
const TAB_STORAGE_KEY = 'real_estate_commission_support_tab';
const DEFAULT_TAB = 'para_enviar';
const DEFAULT_FILTERS = {
  filtro_endereco: '',
  filtro_numero: '',
  filtro_gerente: [],
  filtro_responsavel: [],
  filtro_loja: [],
  filtro_tipo_venda: [],
  ordenacao: '',
};

const readStoredValue = (key, fallback) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || 'null');
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

const readFilters = () => ({
  ...DEFAULT_FILTERS,
  ...readStoredValue(STORAGE_KEY, {}),
});

export function useRealEstateCommissionSupport({
  notify,
  t = key => key,
} = {}) {
  const tab = ref(readStoredValue(TAB_STORAGE_KEY, DEFAULT_TAB));
  const filters = ref(readFilters());
  const catalog = ref({
    addresses: [],
    managers: [],
    responsibles: [],
    stores: [],
    saleTypes: [],
  });
  const rows = ref([]);
  const page = ref(1);
  const meta = ref({ count: 0, current_page: 1, per_page: 25, total_pages: 0 });
  const loading = ref(false);
  const loadingCatalog = ref(false);
  const error = ref('');
  const lastUpdatedAt = ref(null);
  const currentTime = ref(Date.now());
  const noticeVisible = ref(
    readStoredValue('real_estate_commission_support_notice', true)
  );
  let refreshTimer;

  const totalRows = computed(() => meta.value.count || 0);
  const totalPages = computed(() => meta.value.total_pages || 0);
  const minutesSinceRefresh = computed(() => {
    if (!lastUpdatedAt.value) return null;

    return Math.max(
      0,
      Math.floor((currentTime.value - lastUpdatedAt.value.getTime()) / 60000)
    );
  });
  const notifyError = message => {
    error.value = message;
    if (notify) notify(message, 'error');
  };

  const requestParams = () => ({
    ...filters.value,
    envio_planilha: tab.value === 'enviadas' ? 1 : 0,
    page: page.value,
    per_page: meta.value.per_page,
  });

  const load = async () => {
    loading.value = true;
    error.value = '';
    try {
      const response =
        await RealEstateCommissionSupportAPI.list(requestParams());
      rows.value = response.data?.data || [];
      meta.value = {
        ...meta.value,
        ...(response.data?.meta || {}),
      };
      lastUpdatedAt.value = new Date();
      currentTime.value = Date.now();
    } catch {
      notifyError(t('REAL_ESTATE_COMMISSION_SUPPORT.ERRORS.LOAD'));
    } finally {
      loading.value = false;
    }
  };

  const loadCatalog = async () => {
    loadingCatalog.value = true;
    try {
      const [addressesResponse, managersResponse] = await Promise.all([
        RealEstateCommissionSupportAPI.addressCatalog({
          envio_planilha: tab.value === 'enviadas' ? 1 : 0,
        }),
        RealEstateCommissionSupportAPI.managerCatalog(),
      ]);
      catalog.value = {
        addresses: addressesResponse.data?.data || [],
        managers: managersResponse.data?.data?.managers || [],
        responsibles: managersResponse.data?.data?.responsibles || [],
        stores: managersResponse.data?.data?.stores || [],
        saleTypes: managersResponse.data?.data?.sale_types || [],
      };
    } catch {
      notifyError(t('REAL_ESTATE_COMMISSION_SUPPORT.ERRORS.CATALOG'));
    } finally {
      loadingCatalog.value = false;
    }
  };

  const setTab = value => {
    tab.value = value;
    page.value = 1;
    window.localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(value));
  };

  const applyFilters = value => {
    filters.value = { ...DEFAULT_FILTERS, ...value };
    page.value = 1;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters.value));
  };

  const clearFilters = () => {
    applyFilters({});
  };

  const changePage = value => {
    page.value = Math.max(1, value);
  };

  const dismissNotice = () => {
    noticeVisible.value = false;
    window.localStorage.setItem(
      'real_estate_commission_support_notice',
      JSON.stringify(false)
    );
  };

  onMounted(() => {
    refreshTimer = window.setInterval(() => {
      currentTime.value = Date.now();
    }, 60000);
    loadCatalog();
    load();
  });
  onBeforeUnmount(() => window.clearInterval(refreshTimer));

  watch([tab, filters, page], load, { deep: true });
  watch(tab, loadCatalog);

  return {
    tab,
    filters,
    catalog,
    rows,
    page,
    meta,
    loading,
    loadingCatalog,
    error,
    lastUpdatedAt,
    currentTime,
    minutesSinceRefresh,
    noticeVisible,
    totalRows,
    totalPages,
    load,
    setTab,
    applyFilters,
    clearFilters,
    changePage,
    dismissNotice,
  };
}
